import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLead, BrutalButton } from "./shared";
import { LINKS, PRICING } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const professions = [
  "Coach",
  "Consultant",
  "Online Teacher / Trainer",
  "Course Creator",
  "Freelancer / Service Provider",
  "Digital Product Seller",
  "Other Expert / Entrepreneur",
];

export default function LeadModal() {
  const { open, closeLead } = useLead();
  const [form, setForm] = useState({ name: "", email: "", phone: "", profession: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "offer_form" });
      setDone(true);
      toast.success("Thanks! Ratish's team will reach out shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or message on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ name: "", email: "", phone: "", profession: "", message: "" });
    closeLead();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="lead-modal"
        >
          <div className="absolute inset-0 bg-brand-black/70 backdrop-blur-sm" onClick={reset} />
          <motion.div
            className="relative w-full sm:max-w-[520px] bg-brand-cream border-2 border-brand-black rounded-t-[28px] sm:rounded-[28px] shadow-[10px_12px_0_#0B31EB] max-h-[92vh] overflow-y-auto"
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }}
          >
            <button
              onClick={reset}
              aria-label="Close"
              data-testid="lead-modal-close"
              className="absolute right-4 top-4 h-10 w-10 flex items-center justify-center rounded-full border-2 border-brand-black bg-white hover:bg-brand-yellow transition-colors"
            >
              <X size={18} />
            </button>

            {!done ? (
              <div className="p-6 sm:p-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Join the Current Batch</p>
                <h3 className="font-display text-2xl sm:text-3xl mt-2 leading-tight">
                  Reserve Your Seat
                </h3>
                <p className="text-sm text-brand-ink/70 mt-2">
                  Fill this in and pay ₹{PRICING.current} via UPI to confirm. We will guide you through onboarding.
                </p>

                <form onSubmit={submit} className="mt-5 space-y-3.5">
                  <Field label="Full Name" testid="lead-name">
                    <input required value={form.name} onChange={update("name")} data-testid="lead-name-input"
                      className="input-brutal" placeholder="Your name" />
                  </Field>
                  <Field label="Email" testid="lead-email">
                    <input required type="email" value={form.email} onChange={update("email")} data-testid="lead-email-input"
                      className="input-brutal" placeholder="you@email.com" />
                  </Field>
                  <Field label="Phone / WhatsApp" testid="lead-phone">
                    <input required value={form.phone} onChange={update("phone")} data-testid="lead-phone-input"
                      className="input-brutal" placeholder="+91 ..." />
                  </Field>
                  <Field label="You are a..." testid="lead-profession">
                    <select value={form.profession} onChange={update("profession")} data-testid="lead-profession-input"
                      className="input-brutal">
                      <option value="">Select</option>
                      {professions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Anything you'd like to ask? (optional)" testid="lead-message">
                    <textarea value={form.message} onChange={update("message")} rows={2} data-testid="lead-message-input"
                      className="input-brutal resize-none" placeholder="Your niche, goal, or question" />
                  </Field>

                  <div className="rounded-2xl border-2 border-brand-black bg-white p-4 text-sm">
                    <p className="font-bold">Pay via UPI to confirm</p>
                    <p className="mt-1">UPI ID: <span className="font-bold text-brand-blue select-all">{LINKS.upiId}</span></p>
                    <p className="text-brand-ink/70 text-xs mt-1">Payable now: ₹{PRICING.current} (one-time)</p>
                  </div>

                  <BrutalButton as="submit" variant="blue" full data-testid="lead-submit-button">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit & Reserve Seat"}
                  </BrutalButton>
                </form>

                <a href={LINKS.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="lead-whatsapp-link"
                  className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-brand-green hover:underline">
                  <MessageCircle size={16} /> Prefer WhatsApp? Message Ratish directly
                </a>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="mx-auto h-16 w-16 rounded-full bg-brand-green flex items-center justify-center border-2 border-brand-black">
                  <CheckCircle2 className="text-white" size={32} />
                </div>
                <h3 className="font-display text-2xl mt-4">You&rsquo;re on the list!</h3>
                <p className="text-sm text-brand-ink/70 mt-2">
                  Complete your payment to <span className="font-bold text-brand-blue">{LINKS.upiId}</span> and message us on WhatsApp with the screenshot to confirm your seat.
                </p>
                <div className="mt-5 space-y-3">
                  <BrutalButton href={LINKS.whatsapp} variant="green" full data-testid="lead-done-whatsapp">
                    <MessageCircle size={18} /> Send Payment Screenshot
                  </BrutalButton>
                  <BrutalButton onClick={reset} variant="outline" full data-testid="lead-done-close">Close</BrutalButton>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wide text-brand-ink/70">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
